import { useEffect, useState } from "react";

import { apiFetch } from "../../app/api";

interface Check {
  name: string;
  status: string;
  critical: boolean;
  detail: string | null;
}
interface Readiness {
  status: string;
  version: string;
  checks: Check[];
}
interface Instance {
  service: string;
  instance_id: string;
  version: string;
  status: string;
  alive: boolean;
  last_seen_at: string;
}
interface Services {
  services: Instance[];
}
interface Info {
  app_version: string;
  schema_summary: string;
  contracts: Record<string, string>;
  component_versions: Record<string, string>;
  telemetry_enabled: boolean;
}

export function SystemHealthPage() {
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [services, setServices] = useState<Services | null>(null);
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      // /health/ready answers 503 with a full report when a critical check fails.
      fetch("/health/ready", { credentials: "same-origin" }).then((r) => r.json() as Promise<Readiness>),
      apiFetch<Services>("/api/v1/system/services"),
      apiFetch<Info>("/api/v1/system/info"),
    ])
      .then(([ready, svc, sys]) => {
        setReadiness(ready);
        setServices(svc);
        setInfo(sys);
      })
      .catch((caught: unknown) => setError(String(caught)));
  }, []);

  if (error) return <p role="alert">Could not load system health: {error}</p>;
  if (!readiness || !services || !info) return <p>Loading…</p>;

  return (
    <>
      <h1>System health</h1>
      <p>
        <a href="/api/v1/system/diagnostics" download>
          Download diagnostics bundle
        </a>{" "}
        (redacted: contains versions, health, and configuration, never secret values)
      </p>
      <p>
        API <strong className={`status-${readiness.status}`}>{readiness.status}</strong> · version{" "}
        {info.app_version} · {info.schema_summary} · telemetry{" "}
        {info.telemetry_enabled ? "enabled" : "disabled"}
      </p>
      <h2>Readiness checks</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Check</th>
            <th>Status</th>
            <th>Critical</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {readiness.checks.map((check) => (
            <tr key={check.name}>
              <td>{check.name}</td>
              <td className={`status-${check.status}`}>{check.status}</td>
              <td>{check.critical ? "yes" : "no"}</td>
              <td>{check.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2>Background services</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Service</th>
            <th>Instance</th>
            <th>Version</th>
            <th>State</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {services.services.map((instance) => (
            <tr key={`${instance.service}/${instance.instance_id}`}>
              <td>{instance.service}</td>
              <td>{instance.instance_id}</td>
              <td>{instance.version}</td>
              <td className={instance.alive ? `status-${instance.status}` : "status-stale"}>
                {instance.alive ? instance.status : "stale"}
              </td>
              <td>{instance.last_seen_at}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2>Versions</h2>
      <table className="grid">
        <tbody>
          {Object.entries({ ...info.contracts, ...info.component_versions }).map(([name, version]) => (
            <tr key={name}>
              <td>{name}</td>
              <td>{version}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
